import { useState, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import HTMLFlipBook from "react-pageflip";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function PdfPage({ pageNumber, width, height }) {
  return (
    <div style={{ width, height, overflow: "hidden", background: "#fff" }}>
      <Page
        pageNumber={pageNumber}
        width={width}
        renderTextLayer={false}
        renderAnnotationLayer={false}
      />
    </div>
  );
}

export default function FlipBook({ pdfUrl }) {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageWidth, setPageWidth] = useState(480);
  const [scale, setScale] = useState(1);
  const bookRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const containerW = containerRef.current.offsetWidth;
        const w = Math.min(containerW - 96, 600);
        setPageWidth(Math.max(w, 300));
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const scaledWidth = Math.round(pageWidth * scale);
  const scaledHeight = Math.round(scaledWidth * 9 / 16);

  const onDocumentLoad = ({ numPages }) => setNumPages(numPages);
  const onFlip = (e) => setCurrentPage(e.data);

  const goNext = () => bookRef.current?.pageFlip().flipNext();
  const goPrev = () => bookRef.current?.pageFlip().flipPrev();

  return (
    <div ref={containerRef} className="w-full">
      {/* Controls */}
      <div className="flex items-center justify-between mb-4 px-2">
        <span className="font-mono text-xs text-gray-400">
          {numPages
            ? `Trang ${currentPage + 1} / ${numPages}`
            : "Đang tải..."}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setScale((s) => Math.max(0.5, +(s - 0.1).toFixed(1)))}
            className="w-8 h-8 rounded-xl border-2 border-beige-200 bg-white flex items-center justify-center text-gray-500 hover:border-beige-300 hover:shadow transition-all"
          >
            <ZoomOut size={14} />
          </button>
          <span className="font-mono text-xs text-gray-400 w-10 text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={() => setScale((s) => Math.min(1.5, +(s + 0.1).toFixed(1)))}
            className="w-8 h-8 rounded-xl border-2 border-beige-200 bg-white flex items-center justify-center text-gray-500 hover:border-beige-300 hover:shadow transition-all"
          >
            <ZoomIn size={14} />
          </button>
        </div>
      </div>

      {/* Book + nav buttons */}
      <div className="flex items-center justify-center gap-4">
        <button
          onClick={goPrev}
          disabled={currentPage === 0}
          className="w-10 h-10 rounded-2xl border-2 border-beige-200 bg-white flex items-center justify-center text-gray-500 hover:border-beige-300 hover:shadow transition-all disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
        >
          <ChevronLeft size={18} />
        </button>

        <div style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.12)", borderRadius: 12, overflow: "hidden" }}>
          <Document file={pdfUrl} onLoadSuccess={onDocumentLoad}>
            {numPages && (
              <HTMLFlipBook
                ref={bookRef}
                width={scaledWidth}
                height={scaledHeight}
                size="fixed"
                minWidth={280}
                maxWidth={700}
                minHeight={158}
                maxHeight={394}
                showCover={true}
                usePortrait={true}
                startPage={0}
                flippingTime={700}
                useMouseEvents={true}
                onFlip={onFlip}
              >
                {Array.from({ length: numPages }, (_, i) => (
                  <div key={i} style={{ width: scaledWidth, height: scaledHeight }}>
                    <PdfPage pageNumber={i + 1} width={scaledWidth} height={scaledHeight} />
                  </div>
                ))}
              </HTMLFlipBook>
            )}
          </Document>
        </div>

        <button
          onClick={goNext}
          disabled={numPages !== null && currentPage >= numPages - 1}
          className="w-10 h-10 rounded-2xl border-2 border-beige-200 bg-white flex items-center justify-center text-gray-500 hover:border-beige-300 hover:shadow transition-all disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Dot indicators */}
      {numPages && numPages > 1 && (
        <div className="flex justify-center gap-1.5 mt-5 flex-wrap">
          {Array.from({ length: numPages }, (_, i) => (
            <button
              key={i}
              onClick={() => bookRef.current?.pageFlip().flip(i)}
              className={`h-1.5 rounded-full transition-all duration-200 ${
                currentPage === i
                  ? "bg-sky-medium w-4"
                  : "w-1.5 bg-beige-300 hover:bg-beige-400"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
